import { Box, Flex, Text } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useRecoilValue } from "recoil";
import { useEffect } from "react";

import { Moment } from "../Moment";
import { TimePill } from "../styledComponents";
import { TMoment, TMatch } from "MVVM/Views/MatchHistory/@types";
import { MomentsViewModel } from "./ViewModel";
import { MomentsLoading } from "../../MatchesTableLoading";
import { NoMoments } from "../NoMoments";
import {
  MatchHistoryGame,
  MatchHistoryWidgetSourceState,
} from "MVVM/State/matchHistory";
import { usePortal } from "MVVM/Hooks/usePortal";
import { MatchHistoryLoginModal } from "../../LoginModal";
import { useUser } from "MVVM/Hooks/useUser";

const MomentsList = ({
  match,
  isWidget,
  outdated,
  numMomentsToRender,
}: {
  match: TMatch;
  isWidget?: boolean;
  outdated: boolean;
  numMomentsToRender?: number;
}) => {
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const limit = isWidget ? 3 : 20;
  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const { allstarUser } = useUser();
  const momentIdQueryParam = new URLSearchParams(window.location.search).get(
    "momentId",
  );
  const { moments, loading, requestOnDemandMoments } = MomentsViewModel(
    match.id,
    limit,
  );
  const momentForModal = moments.find(
    (moment: TMoment) => moment.id === momentIdQueryParam,
  );

  const {
    portalOpen: matchHistoryLoginPortalOpen,
    setPortalOpen: setMatchHistoryLoginPortalOpen,
    Portal: MatchHistoryLoginPortal,
  } = usePortal(
    <MatchHistoryLoginModal
      game={matchHistoryGame}
      moment={momentForModal}
      matchTitle={match.title}
      title="matchHistoryLoginModal.title"
      defaultTitle="Don't Miss Out On This Play!"
      subtitle="matchHistoryLoginModal.subtitle"
      defaultSubtitle="Create a FREE Allstar.gg account to create, edit, and share a clip from this moment."
    />,
  );

  const momentsToRender = numMomentsToRender
    ? moments.slice(0, numMomentsToRender)
    : moments;

  useEffect(() => {
    if (!allstarUser.loggedIn && (momentForModal || match.title)) {
      if (!sessionStorage.getItem("mh_signup_modal_on_land_shown")) {
        sessionStorage.setItem("mh_signup_modal_on_land_shown", "true");
        setMatchHistoryLoginPortalOpen(true);
      }
      // else do nothing, modal already shown this session
    }
  }, [
    allstarUser.loggedIn,
    momentForModal,
    match.title,
    setMatchHistoryLoginPortalOpen,
  ]);

  return (
    <>
      {matchHistoryLoginPortalOpen && MatchHistoryLoginPortal}
      {loading && <MomentsLoading />}
      {!loading && !moments.length && (
        <NoMoments requestOnDemandMoments={requestOnDemandMoments} />
      )}
      {!loading &&
        !!moments.length &&
        momentsToRender.map((moment: TMoment) => (
          <MomentContainer
            key={moment.id}
            $widgetSource={widgetSource}
            numMomentsToRender={numMomentsToRender || 0}
          >
            <Timeline mr="4" className="timeline">
              <TimePill>
                <Text fontSize="0" style={{ minWidth: "auto" }}>
                  {moment.timelineLabel}
                </Text>
              </TimePill>
            </Timeline>
            <Moment
              moment={moment}
              isWidget={isWidget}
              outdated={outdated}
              matchId={match.id}
            />
          </MomentContainer>
        ))}
    </>
  );
};

const MomentContainer = styled(Flex)<{
  $widgetSource: string;
  numMomentsToRender: number;
}>`
  :last-child :first-child.timeline {
    height: 0;
  }

  ${({ $widgetSource, numMomentsToRender }) =>
    $widgetSource === "iframe" &&
    !numMomentsToRender &&
    css`
      @media (max-height: 585px), (max-width: 650px) {
        :last-child {
          display: none;
        }
      }

      @media (max-height: 500px), (max-width: 500px) {
        :nth-last-child(2) {
          display: none;
        }
      }
    `}
`;

const Timeline = styled(Box)`
  background: linear-gradient(
    90deg,
    #0000 98%,
    ${({ theme }) => theme.colors.navy} 98%
  );
  background-size: 52%;
  min-width: 60px;

  width: 60px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: none;
  }
`;

export { MomentsList };
